import { type ReactNode } from 'react'

// SegmentedControl — small radio-group-style switch used for things like
// 2D/3D, map style, light/dark, etc. Compose against design tokens; never
// re-implement these as raw <button> with hand-rolled className.

type Size = 'sm' | 'md'

export interface SegmentedOption<T extends string | number> {
  value: T
  label: ReactNode
  /** Optional data-testid suffix applied to the rendered button. */
  testid?: string
  /** Aria-label for icon-only labels (rare). */
  ariaLabel?: string
}

export interface SegmentedControlProps<T extends string | number> {
  value: T
  options: ReadonlyArray<SegmentedOption<T>>
  onChange: (next: T) => void
  size?: Size
  className?: string
  ariaLabel?: string
}

const SIZE_CLS: Record<Size, string> = {
  sm: 'h-[26px] px-2.5 text-[11px]',
  md: 'h-[30px] px-3.5 text-xs',
}

export function SegmentedControl<T extends string | number>({
  value,
  options,
  onChange,
  size = 'sm',
  className = '',
  ariaLabel,
}: SegmentedControlProps<T>) {
  const sizeCls = SIZE_CLS[size]
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={`inline-flex rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)] overflow-hidden ${className}`.trim()}
    >
      {options.map((opt) => {
        const selected = opt.value === value
        const cls = `${sizeCls} inline-flex items-center justify-center font-medium transition select-none focus-visible:outline-none focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-[var(--xr-brand-primary)] ${
          selected
            ? 'bg-[var(--xr-brand-primary)] text-[var(--xr-brand-primary-ink)]'
            : 'bg-[var(--xr-bg-surface)] text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface-elev)]'
        }`
        return (
          <button
            key={String(opt.value)}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={opt.ariaLabel}
            data-testid={opt.testid}
            onClick={() => onChange(opt.value)}
            className={cls}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}
